import React from 'react';
import styled from 'styled-components';
import { Table, Tag, Button, Space, Popconfirm, Tooltip, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import {
  PiEyeBold,
  PiTrashBold,
  PiMapTrifoldBold,
  PiFolderBold,
  PiCalendarBlankBold,
  PiNotePencilBold,
} from 'react-icons/pi';
import type { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import type { Project_Base } from '~/common/typescript/models/Project';
import { parseFormContent } from '~/components/Map/panels/GeocollectDatatablePanel/utils/CollectFormParser';
import { formatDate } from '~/utils/dateUtils';
import Routes from '~/routes/routes';

interface CollectFormListProps {
  forms: CollectForm_Base[];
  projectsMap: Map<number, Project_Base>;
  loading: boolean;
  onViewDetail: (form: CollectForm_Base) => void;
  onDelete: (uuid: string) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export const CollectFormList: React.FC<CollectFormListProps> = ({
  forms,
  projectsMap,
  loading,
  onViewDetail,
  onDelete,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const columns: ColumnsType<CollectForm_Base> = [
    {
      title: 'Tên biểu mẫu',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <UI_NameCell>
          <UI_NameText onClick={() => onViewDetail(record)}>{name || 'Chưa đặt tên'}</UI_NameText>
          {record.description && (
            <UI_DescText title={record.description}>{record.description}</UI_DescText>
          )}
        </UI_NameCell>
      ),
    },
    {
      title: 'Dự án',
      key: 'project',
      width: 220,
      render: (_, record) => {
        const project = projectsMap.get(record.project_id);
        const projectName = project ? project.name : `Dự án #${record.project_id}`;
        return (
          <UI_ProjectBadge title={projectName}>
            <PiFolderBold size={13} />
            <span>{projectName}</span>
          </UI_ProjectBadge>
        );
      },
    },
    {
      title: 'Phiên bản',
      key: 'version',
      width: 120,
      render: (_, record) => {
        const parsed = parseFormContent(record);
        const isV2 = record.form_version === 2 || parsed?.version === 'v2';
        return <Tag color={isV2 ? 'cyan' : 'orange'}>{isV2 ? 'V2' : 'V1'}</Tag>;
      },
    },
    {
      title: 'Số trường',
      key: 'field_count',
      width: 120,
      render: (_, record) => {
        const parsed = parseFormContent(record);
        const count = parsed?.fields?.length || 0;
        return <Tag color="blue">{count} trường</Tag>;
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        <UI_DateText>
          <PiCalendarBlankBold size={13} />
          <span>{formatDate(date)}</span>
        </UI_DateText>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 160,
      align: 'right',
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Xem chi tiết biểu mẫu">
            <Button
              size="small"
              type="primary"
              ghost
              icon={<PiEyeBold size={14} />}
              onClick={() => onViewDetail(record)}
            >
              Chi tiết
            </Button>
          </Tooltip>

          <Tooltip title="Thiết kế biểu mẫu">
            <Button
              size="small"
              icon={<PiNotePencilBold size={14} />}
              onClick={() =>
                navigate(
                  `${Routes.COLLECT_FORM_DESIGN}?formUuid=${record.uuid}&projectId=${record.project_id}`,
                )
              }
            />
          </Tooltip>

          <Tooltip title="Mở trong bản đồ dự án">
            <Button
              size="small"
              icon={<PiMapTrifoldBold size={14} />}
              onClick={() => navigate(`${Routes.PROJECT}/${record.project_id}`)}
            />
          </Tooltip>

          <Popconfirm
            title="Xoá biểu mẫu"
            description="Bạn có chắc chắn muốn xoá biểu mẫu thu thập này không?"
            onConfirm={() => onDelete(record.uuid)}
            okText="Xoá"
            cancelText="Huỷ"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger type="text" icon={<PiTrashBold size={14} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <UI_TableWrapper>
      <Table
        dataSource={forms}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        size="middle"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} biểu mẫu`,
          onChange: onPageChange,
        }}
        locale={{
          emptyText: <Empty description="Không có biểu mẫu nào trong dự án đã chọn" />,
        }}
      />
    </UI_TableWrapper>
  );
};

const UI_TableWrapper = styled.div`
  padding: 16px 20px;

  .ant-table-wrapper {
    background-color: #fff;
    border-radius: 8px;
    border: 1px solid #f1f5f9;
  }
`;

const UI_NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UI_NameText = styled.span`
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const UI_DescText = styled.span`
  font-size: 12px;
  color: #64748b;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_ProjectBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #0284c7;
  background-color: #f0f9ff;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_DateText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
`;
